import { createTheme, Theme } from '@mui/material/styles';

// ── Font stacks ──────────────────────────────────────────────────────
// Space Grotesk (headings/brand) + Inter (body/UI) + IBM Plex Mono (data).
// Loaded via Google Fonts in glassmorphism.css.
const fontHeading = '"Space Grotesk", "Inter", "Helvetica Neue", Arial, sans-serif';
const fontBody = '"Inter", "Helvetica Neue", Arial, sans-serif';
const fontMono = '"IBM Plex Mono", "SF Mono", "Roboto Mono", Consolas, monospace';

// Bloomberg color palette using CSS variables — values change with data-theme attribute
export const bloomberg = {
  font: {
    heading: fontHeading,
    body: fontBody,
    mono: fontMono,
  },
  // 7-step type scale (px). Replaces the ~28 ad hoc fontSize values that had
  // accumulated across components — pick from here instead of eyeballing a number.
  fontSize: {
    micro: 10,       // table headers, uppercase eyebrow labels
    caption: 11,     // timestamps, secondary meta
    bodyDense: 12.5, // table cells, list rows — the app's default working size
    body: 14,        // paragraphs, descriptions, form labels
    panelTitle: 16,  // card/panel headers
    section: 20,     // page-section headings
    display: 26,     // hero numbers, page titles
  },
  // Core backgrounds
  bg: {
    primary: 'var(--bb-bg-primary)',
    secondary: 'var(--bb-bg-secondary)',
    tertiary: 'var(--bb-bg-tertiary)',
    hover: 'var(--bb-bg-hover)',
    input: 'var(--bb-bg-input)',
  },
  // Text
  text: {
    primary: 'var(--bb-text-primary)',
    secondary: 'var(--bb-text-secondary)',
    muted: 'var(--bb-text-muted)',
    inverse: 'var(--bb-text-inverse)',
  },
  // Signature accent colors
  accent: {
    orange: 'var(--bb-accent)',
    orangeLight: 'var(--bb-accent-light)',
    orangeDim: 'var(--bb-accent-dim)',
    blue: 'var(--bb-blue)',
    blueDim: 'var(--bb-blue-dim)',
    cyan: '#22d3ee',
  },
  // Status colors (universal)
  status: {
    positive: '#22c55e',
    positiveBg: 'var(--bb-status-positive-bg)',
    negative: '#ef4444',
    negativeBg: 'var(--bb-status-negative-bg)',
    warning: '#f59e0b',
    warningBg: 'var(--bb-status-warning-bg)',
    info: 'var(--bb-blue)',
    infoBg: 'var(--bb-status-info-bg)',
  },
  // Borders
  border: {
    primary: 'var(--bb-border-primary)',
    secondary: 'var(--bb-border-secondary)',
    accent: 'var(--bb-border-accent)',
  },
  // Chart colors
  chart: {
    grid: 'var(--bb-chart-grid)',
    axis: 'var(--bb-chart-axis)',
    tooltip: 'var(--bb-chart-tooltip)',
    series: ['var(--bb-accent)', '#3b82f6', '#22c55e', '#ef4444', '#a855f7', '#22d3ee', '#f59e0b', '#ec4899'],
  },
};

// ── Raw color palettes (hex values for MUI createTheme) ──────────────

const darkColors = {
  accent: '#ff8c00',
  accentLight: '#ffb347',
  accentDark: '#cc7000',
  accentDim: 'rgba(255, 140, 0, 0.15)',
  accentOnBg: '#000',
  blue: '#3b82f6',
  blueDim: 'rgba(59, 130, 246, 0.15)',
  // Consolidated to 3 functional roles (base / recessed / hover) instead of
  // 5 independently-stepped shades — page and card now share one base tone,
  // separated only by border, so containers stop reading as visually
  // different from the page around them.
  bgPrimary: '#0d0f14',
  bgSecondary: '#0d0f14',
  bgTertiary: '#171a21',
  bgHover: '#21252e',
  bgInput: '#171a21',
  textPrimary: '#e2e8f0',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  borderPrimary: '#1e293b',
  borderSecondary: '#334155',
  mode: 'dark' as const,
};

const lightColors = {
  accent: '#0016e0',
  accentLight: '#3344ff',
  accentDark: '#000fb0',
  accentDim: 'rgba(0, 22, 224, 0.10)',
  accentOnBg: '#fff',
  blue: '#3b82f6',
  blueDim: 'rgba(59, 130, 246, 0.10)',
  bgPrimary: '#ffffff',
  bgSecondary: '#f5f7fa',
  bgTertiary: '#ebeef3',
  bgHover: '#e8edf3',
  bgInput: '#f0f2f5',
  textPrimary: '#1a1a2e',
  textSecondary: '#4a5568',
  textMuted: '#718096',
  borderPrimary: '#e2e8f0',
  borderSecondary: '#cbd5e1',
  mode: 'light' as const,
};

// ── MUI theme factory ────────────────────────────────────────────────

function buildTheme(c: Omit<typeof darkColors, 'mode'> & { mode: 'dark' | 'light' }): Theme {
  return createTheme({
    palette: {
      mode: c.mode,
      primary: {
        main: c.accent,
        light: c.accentLight,
        dark: c.accentDark,
      },
      secondary: {
        main: c.blue,
        light: '#60a5fa',
        dark: '#2563eb',
      },
      background: {
        default: c.bgPrimary,
        paper: c.bgSecondary,
      },
      text: {
        primary: c.textPrimary,
        secondary: c.textSecondary,
        disabled: c.textMuted,
      },
      success: { main: '#22c55e' },
      error: { main: '#ef4444' },
      warning: { main: '#f59e0b' },
      info: { main: c.blue },
      divider: c.borderPrimary,
    },
    typography: {
      fontFamily: fontBody,
      h1: { fontFamily: fontHeading, fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontFamily: fontHeading, fontWeight: 700, letterSpacing: '-0.01em' },
      h3: { fontFamily: fontHeading, fontWeight: 600 },
      h4: { fontFamily: fontHeading, fontWeight: 600, letterSpacing: '-0.01em' },
      h5: { fontFamily: fontHeading, fontWeight: 600 },
      h6: { fontFamily: fontHeading, fontWeight: 600 },
      subtitle1: { fontFamily: fontHeading, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase' as const, fontSize: '0.8rem' },
      subtitle2: { fontFamily: fontHeading, fontWeight: 600, letterSpacing: '0.01em', fontSize: '0.75rem' },
      body1: { fontSize: '0.875rem', lineHeight: 1.6 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
      caption: { fontSize: '0.7rem', letterSpacing: '0.03em' },
      button: { textTransform: 'none' as const, fontWeight: 600, letterSpacing: '0.02em' },
    },
    shape: { borderRadius: 6 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: c.bgPrimary,
            color: c.textPrimary,
            scrollbarWidth: 'thin',
            scrollbarColor: `${c.borderSecondary} ${c.bgPrimary}`,
            '&::-webkit-scrollbar': { width: '6px', height: '6px' },
            '&::-webkit-scrollbar-track': { background: c.bgPrimary },
            '&::-webkit-scrollbar-thumb': { background: c.borderSecondary, borderRadius: '3px' },
          },
          '*': { scrollbarWidth: 'thin', scrollbarColor: `${c.borderSecondary} transparent` },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none', backgroundColor: c.bgSecondary, border: `1px solid ${c.borderPrimary}`, borderRadius: 6 },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { backgroundImage: 'none', backgroundColor: c.bgSecondary, border: `1px solid ${c.borderPrimary}`, borderRadius: 6 },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { backgroundImage: 'none', backgroundColor: `${c.bgPrimary} !important`, borderBottom: `1px solid ${c.borderPrimary}`, boxShadow: 'none' },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 4, fontWeight: 600, fontSize: '0.8rem', padding: '6px 16px' },
          contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
          containedPrimary: {
            backgroundColor: c.accent,
            color: c.accentOnBg,
            '&:hover': { backgroundColor: c.accentLight },
          },
          outlined: {
            borderColor: c.borderSecondary,
            color: c.textPrimary,
            '&:hover': { borderColor: c.accent, backgroundColor: c.accentDim },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { color: c.textSecondary, '&:hover': { backgroundColor: c.bgHover, color: c.accent } },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 4, fontWeight: 600, fontSize: '0.7rem', height: 26 },
          filled: { backgroundColor: c.bgTertiary, color: c.textPrimary, border: `1px solid ${c.borderPrimary}` },
          outlined: { borderColor: c.borderSecondary },
        },
      },
      MuiTableContainer: { styleOverrides: { root: { backgroundColor: 'transparent' } } },
      MuiTable: { styleOverrides: { root: { backgroundColor: 'transparent' } } },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: c.bgTertiary, color: c.accent, fontWeight: 700, fontSize: '0.7rem',
              letterSpacing: '0.05em', textTransform: 'uppercase', borderBottom: `2px solid ${c.accent}`,
              padding: '8px 12px', whiteSpace: 'nowrap',
            },
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-body': {
              fontSize: '0.8rem', fontFamily: fontMono,
              padding: '6px 12px', borderBottom: `1px solid ${c.borderPrimary}`, color: c.textPrimary,
            },
            '& .MuiTableRow-root:hover': { backgroundColor: `${c.bgHover} !important` },
          },
        },
      },
      MuiTableCell: { styleOverrides: { root: { borderBottom: `1px solid ${c.borderPrimary}` } } },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: c.bgInput, fontSize: '0.85rem',
              '& fieldset': { borderColor: c.borderPrimary },
              '&:hover fieldset': { borderColor: c.borderSecondary },
              '&.Mui-focused fieldset': { borderColor: c.accent, borderWidth: '1px' },
            },
            '& .MuiInputLabel-root': {
              color: c.textMuted, fontSize: '0.85rem',
              '&.Mui-focused': { color: c.accent },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: c.bgInput,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: c.borderPrimary },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: c.borderSecondary },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.accent, borderWidth: '1px' },
          },
        },
      },
      MuiDialog: {
        styleOverrides: { paper: { backgroundColor: c.bgSecondary, border: `1px solid ${c.borderSecondary}`, backgroundImage: 'none' } },
      },
      MuiMenu: {
        styleOverrides: { paper: { backgroundColor: c.bgSecondary, border: `1px solid ${c.borderPrimary}`, backgroundImage: 'none' } },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.85rem',
            '&:hover': { backgroundColor: c.bgHover },
            '&.Mui-selected': { backgroundColor: c.accentDim, '&:hover': { backgroundColor: c.accentDim } },
          },
        },
      },
      MuiTabs: {
        styleOverrides: { root: { minHeight: 36 }, indicator: { backgroundColor: c.accent, height: 2 } },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 36, fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.05em',
            textTransform: 'uppercase', color: c.textMuted, '&.Mui-selected': { color: c.accent },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderColor: c.borderPrimary, color: c.textSecondary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'none',
            '&.Mui-selected': {
              backgroundColor: c.accentDim, color: c.accent, borderColor: c.accent,
              '&:hover': { backgroundColor: c.accentDim },
            },
            '&:hover': { backgroundColor: c.bgHover },
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: { root: { backgroundColor: c.bgInput, border: `1px solid ${c.borderPrimary}`, borderRadius: 4 } },
      },
      MuiTooltip: {
        styleOverrides: { tooltip: { backgroundColor: c.bgTertiary, border: `1px solid ${c.borderSecondary}`, fontSize: '0.75rem', color: c.textPrimary } },
      },
      MuiSkeleton: { styleOverrides: { root: { backgroundColor: c.bgTertiary } } },
      MuiCircularProgress: { styleOverrides: { root: { color: c.accent } } },
      MuiLinearProgress: {
        styleOverrides: {
          root: { backgroundColor: c.bgTertiary, borderRadius: 2 },
          bar: { backgroundColor: c.accent, borderRadius: 2 },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 4 },
          standardSuccess: { backgroundColor: 'rgba(34, 197, 94, 0.12)', color: '#22c55e' },
          standardError: { backgroundColor: 'rgba(239, 68, 68, 0.12)', color: '#ef4444' },
          standardWarning: { backgroundColor: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b' },
          standardInfo: { backgroundColor: c.blueDim, color: c.blue },
        },
      },
      MuiDivider: { styleOverrides: { root: { borderColor: c.borderPrimary } } },
      MuiAccordion: {
        styleOverrides: { root: { backgroundColor: c.bgSecondary, border: `1px solid ${c.borderPrimary}`, '&:before': { display: 'none' } } },
      },
      MuiSwitch: {
        styleOverrides: { root: { '& .Mui-checked': { color: c.accent }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: c.accent } } },
      },
      MuiSlider: { styleOverrides: { root: { color: c.accent } } },
      MuiAutocomplete: {
        styleOverrides: { paper: { backgroundColor: c.bgSecondary, border: `1px solid ${c.borderPrimary}` } },
      },
      MuiPopover: {
        styleOverrides: { paper: { backgroundColor: c.bgSecondary, border: `1px solid ${c.borderPrimary}`, backgroundImage: 'none' } },
      },
    },
  });
}

export const darkTheme = buildTheme(darkColors);
export const lightTheme = buildTheme(lightColors);

// Default export for backwards compat
export default darkTheme;
